import { useEffect, useState, useCallback } from "react";

/* This library provides a useExternalScript hook that a React component
  can call to load a script from an external URL.

  It will return one of the following three exported constants:
  SCRIPT_LOADING, SCRIPT_READY, or SCRIPT_ERROR.

  If the component does not yet know what URL to load from, but needs to
  call the hook anyway (because hooks cannot be called conditionally), it
  may specify url=null and the hook will then do nothing but return
  SCRIPT_LOADING.

*/

export const SCRIPT_LOADING = "loading";
export const SCRIPT_READY = "ready";
export const SCRIPT_ERROR = "error";

export function useExternalScript(url) {
  // To avoid conflict with a stale state if the calling component changes
  // the URL, we keep state as an object, with a slice for each URL value.

  const [scriptState, setScriptState] = useState({});

  const setStateForUrl = useCallback(
    (value) =>
      setScriptState((state) => {
        return { ...state, [url]: value };
      }),
    [url]
  );

  // When the hook is first called for a particular non-null URL, the
  // state entry will be undefined. In this case we look to see if a
  // script element with that URL already exists in the document. If it
  // does not, we fire an effect that will load it, set callbacks to adjust
  // the state once loaded, and return a cleanup function to remove the
  // callbacks.

  const uninitialized = url !== null && scriptState[url] === undefined;

  const existingScript = uninitialized
    ? document.querySelector(`script[src="${url}"]`)
    : null;

  const loadRequired =
    url !== null &&
    scriptState[url] !== SCRIPT_READY &&
    scriptState[url] !== SCRIPT_ERROR &&
    !existingScript;

  useEffect(() => {
    if (existingScript) setStateForUrl(SCRIPT_READY);
  }, [existingScript, setStateForUrl]);

  const handleScriptEvent = useCallback(
    (event) =>
      setStateForUrl(event.type === "load" ? SCRIPT_READY : SCRIPT_ERROR),
    [setStateForUrl]
  );

  useEffect(() => {
    if (loadRequired) {
      const script = document.createElement("script");
      script.type = "application/javascript";
      script.src = url;
      script.async = true;
      document.body.appendChild(script);
      script.addEventListener("load", handleScriptEvent);
      script.addEventListener("error", handleScriptEvent);

      script.addEventListener("load", handleScriptEvent);
      script.addEventListener("error", handleScriptEvent);

      return () => {
        script.removeEventListener("load", handleScriptEvent);
        script.removeEventListener("error", handleScriptEvent);
      };
    } else return () => {};
  }, [loadRequired, url, handleScriptEvent]);

  return url === null || uninitialized ? SCRIPT_LOADING : scriptState[url];
}
